import { KeyValuePipe } from '@angular/common';
import {
	ChangeDetectorRef,
	Component,
	Input,
	OnChanges,
	OnDestroy,
	OnInit,
	SimpleChanges,
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { AsyncSubject, Subject, timer } from 'rxjs';
import {
	distinctUntilChanged,
	filter,
	map,
	skip,
	switchMap,
	takeUntil,
} from 'rxjs/operators';
import { getEnvironment } from 'src/lib/environment/environment';
import { ChannelModel } from 'src/lib/services/api/gabby/channels/channel.model';
import { RealtimeService } from 'src/lib/services/api/realtime/realtime.service';
import { GabbyWebsocketEventFactoryService } from 'src/lib/services/websocket/websocket-event-factory/gabby/gabby-websocket-event-factory.service';
import { WebsocketService } from 'src/lib/services/websocket/websocket.service';
import { FormControlWrapper } from 'src/lib/types/forms.def';
import { MessageForm } from '../gabby-chat-component-definitions';

@Component({
	selector: 'ae-gabby-chat-typing-notification',
	templateUrl: './gabby-chat-typing-notification.component.html',
	styleUrls: ['./gabby-chat-typing-notification.component.scss'],
	imports: [KeyValuePipe],
})
export class GabbyChatTypingNotificationComponent
	implements OnInit, OnDestroy, OnChanges
{
	private _unsubscribe$ = new AsyncSubject<null>();

	public notifyTyping$ = new Subject<boolean>();

	@Input() messageForm: FormGroup<FormControlWrapper<MessageForm>>;
	@Input() channel: ChannelModel;
	@Input() viewingUserLinkId: number;

	public channelTypingUserLinkIds = new Map<number, Date>();

	constructor(
		private cdr: ChangeDetectorRef,
		private wefs: GabbyWebsocketEventFactoryService,
		private realtimeService: RealtimeService,
		private websocketService: WebsocketService,
	) {}

	ngOnInit() {
		// Listen for typing notifications
		this.wefs
			.channelUserTyping$()
			.pipe(takeUntil(this._unsubscribe$))
			.subscribe((e) => {
				if (
					this.channel != null &&
					e.channelId === this.channel.id &&
					e.userLinkId !== this.viewingUserLinkId
				) {
					if (this.channelTypingUserLinkIds.has(e.userLinkId)) {
						if (e.active) {
							this.channelTypingUserLinkIds.set(e.userLinkId, new Date());
						} else {
							this.channelTypingUserLinkIds.delete(e.userLinkId);
							this.cdr.detectChanges();
						}
					} else {
						if (e.active) {
							this.channelTypingUserLinkIds.set(e.userLinkId, new Date());
							this.cdr.detectChanges();
						}
					}
				}
			});

		this.notifyTyping$
			.pipe(
				distinctUntilChanged(),
				switchMap((x) => {
					return timer(x ? 500 : 100).pipe(map(() => x)); // Debounce typing notifications
				}),
				switchMap((x) => {
					return this.realtimeService.sendUserTypingNotification(
						this.channel.participants.map((p) => p.uid),
						this.channel.id,
						this.viewingUserLinkId,
						x,
					);
				}),
				takeUntil(this._unsubscribe$),
			)
			.subscribe();

		this.notifyTyping$
			.pipe(
				filter((x) => x),
				switchMap(() => {
					return timer(
						getEnvironment().settings.services.websocket.gabby
							.typingActivityTimeout,
					);
				}),
			)
			.subscribe(() => {
				this.notifyTyping$.next(false);
			});

		this.websocketService.isConnected$
			.pipe(takeUntil(this._unsubscribe$))
			.subscribe((connected) => {
				if (!connected) {
					this.channelTypingUserLinkIds.clear();
					this.cdr.detectChanges();
				}
			});
	}

	public getName = (userLinkId: number): string => {
		const participant = this.channel.participants.find(
			(p) => p.link_id === userLinkId,
		);
		if (participant != null) {
			return participant.name;
		} else return 'Someone';
	};

	ngOnChanges(changes: SimpleChanges): void {
		if (changes.messageForm) {
			this.messageForm.valueChanges
				.pipe(skip(1), takeUntil(this._unsubscribe$))
				.subscribe((x) => {
					this.notifyTyping$.next(Boolean(x.message && x.message.length > 0));
				});
		}

		if (changes.channel || changes.viewingUserId) {
			this.channelTypingUserLinkIds.clear();
		}
	}

	ngOnDestroy(): void {
		this._unsubscribe$.next(null);
		this._unsubscribe$.complete();
		this._unsubscribe$ = null;
	}
}
